import { Ability, AbilityBuilder, AbilityClass } from '@casl/ability'
import {
  isAutoContractCategory,
  isInStatuses,
  isManualContractCategory,
  isNotInStatuses,
  isOwnerCondition,
  isOwnerInSameHSCondition
} from './utils'
import { ACTIONS, ROLES, SUBJECTS } from 'features/Permission/constants'
import { IUser } from 'redux/store/user/types'
import {
  Conditions,
  ContractCategoryAbility,
  ContractCategoryAbilityBuilder,
  PossibleAbilities
} from 'features/ContractCategoryDetails/Abilities/types'
import { ContractCategoryStatus } from '../../Categories/types'
import { isInsideCommunityCondition } from 'features/Permission/abilities/conditions'

const ContractCategoryAbilityClass =
  Ability as AbilityClass<ContractCategoryAbility>

export const ability = new Ability<PossibleAbilities, Conditions>()

export default (user: IUser) => {
  const builder = new AbilityBuilder(ContractCategoryAbilityClass)

  defineAbilities(builder, user)

  return new Ability(builder.rules) as ContractCategoryAbility
}

const defineCCEditAbilities = (
  builder: ContractCategoryAbilityBuilder,
  abilityCondition: Conditions,
  canEditResponsibleUser = false
) => {
  builder.can(ACTIONS.EDIT, SUBJECTS.CONTRACT_CATEGORY, {
    ...abilityCondition,
    ...isAutoContractCategory(),
    ...isNotInStatuses([
      ContractCategoryStatus.CREATED,
      ContractCategoryStatus.RFP,
      ContractCategoryStatus.EXPIRED,
      ContractCategoryStatus.INACTIVE
    ])
  })

  builder.can(ACTIONS.EDIT, SUBJECTS.CONTRACT_CATEGORY, {
    ...abilityCondition,
    ...isManualContractCategory(),
    ...isNotInStatuses([
      ContractCategoryStatus.RFP,
      ContractCategoryStatus.EXPIRED,
      ContractCategoryStatus.INACTIVE
    ])
  })

  if (canEditResponsibleUser) {
    builder.can(ACTIONS.ADD, SUBJECTS.RESPONSIBLE_USER, {
      ...abilityCondition
    })
    builder.can(ACTIONS.DELETE, SUBJECTS.RESPONSIBLE_USER, {
      ...abilityCondition,
      ...isAutoContractCategory(),
      ...isNotInStatuses([
        ContractCategoryStatus.CREATED,
        ContractCategoryStatus.RFP,
        ContractCategoryStatus.EXPIRED,
        ContractCategoryStatus.INACTIVE
      ])
    })

    builder.can(ACTIONS.DELETE, SUBJECTS.RESPONSIBLE_USER, {
      ...abilityCondition,
      ...isManualContractCategory(),
      ...isNotInStatuses([
        ContractCategoryStatus.RFP,
        ContractCategoryStatus.EXPIRED,
        ContractCategoryStatus.INACTIVE
      ])
    })
    builder.can(ACTIONS.EDIT, SUBJECTS.RESPONSIBLE_USER, {
      ...abilityCondition,
      ...isManualContractCategory(),
      ...isInStatuses([
        ContractCategoryStatus.CREATED,
        ContractCategoryStatus.ACTIVE,
        ContractCategoryStatus.LOADING
      ])
    })
    builder.can(ACTIONS.EDIT, SUBJECTS.RESPONSIBLE_USER, {
      ...abilityCondition,
      ...isAutoContractCategory(),
      ...isInStatuses([
        ContractCategoryStatus.CONTRACTING,
        ContractCategoryStatus.ACTIVE
      ])
    })
  }

  builder.can(ACTIONS.MANAGE, SUBJECTS.FACILITY_USERS, {
    ...abilityCondition,
    ...isManualContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CREATED,
      ContractCategoryStatus.ACTIVE,
      ContractCategoryStatus.LOADING
    ])
  })
  builder.can(ACTIONS.MANAGE, SUBJECTS.FACILITY_USERS, {
    ...abilityCondition,
    ...isAutoContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CONTRACTING,
      ContractCategoryStatus.ACTIVE
    ])
  })
  builder.can(ACTIONS.MANAGE, SUBJECTS.HEALTH_SYSTEMS, {
    ...abilityCondition,
    ...isManualContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CREATED,
      ContractCategoryStatus.ACTIVE,
      ContractCategoryStatus.LOADING
    ])
  })
  builder.can(ACTIONS.MANAGE, SUBJECTS.HEALTH_SYSTEMS, {
    ...abilityCondition,
    ...isAutoContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CONTRACTING,
      ContractCategoryStatus.ACTIVE
    ])
  })
}

const defineSavingsSpendAbilities = (
  builder: ContractCategoryAbilityBuilder,
  abilityCondition: Conditions
) => {
  builder.can(ACTIONS.EDIT, SUBJECTS.EXPECTED_SAVINGS_SPEND, {
    ...abilityCondition,
    ...isManualContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CREATED,
      ContractCategoryStatus.ACTIVE,
      ContractCategoryStatus.LOADING
    ])
  })
  builder.can(ACTIONS.EDIT, SUBJECTS.EXPECTED_SAVINGS_SPEND, {
    ...abilityCondition,
    ...isAutoContractCategory(),
    ...isInStatuses([
      ContractCategoryStatus.CONTRACTING,
      ContractCategoryStatus.ACTIVE
    ])
  })
}

const defineAbilities = (
  builder: ContractCategoryAbilityBuilder,
  user: IUser
) => {
  builder.can(ACTIONS.VIEW, SUBJECTS.HEALTH_SYSTEM, isInsideCommunityCondition)

  if (
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.COMMONS_VICE_PRESIDENT
  ) {
    builder.can(ACTIONS.EDIT, SUBJECTS.OWNER, {
      ...isAutoContractCategory(),
      ...isInStatuses([
        ContractCategoryStatus.CONTRACTING,
        ContractCategoryStatus.LOADING,
        ContractCategoryStatus.ACTIVE
      ])
    })

    builder.can(ACTIONS.EDIT, SUBJECTS.OWNER, {
      ...isManualContractCategory(),
      ...isInStatuses([
        ContractCategoryStatus.CREATED,
        ContractCategoryStatus.CONTRACTING,
        ContractCategoryStatus.LOADING,
        ContractCategoryStatus.ACTIVE
      ])
    })
  }
  if (user.role === ROLES.COMMUNITY_PRESIDENT) {
    builder.can(ACTIONS.VIEW, SUBJECTS.CONTRACT_CATEGORY)
  }
  if (
    user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.COMMONS_CONTRACT_STEWARD ||
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.COMMONS_VICE_PRESIDENT
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.CONTRACT_CATEGORY)
    defineCCEditAbilities(builder, isOwnerCondition(user), true)
  }
  if (user.role === ROLES.ANALYST) {
    defineCCEditAbilities(builder, isOwnerInSameHSCondition(user))
    defineSavingsSpendAbilities(builder, isOwnerInSameHSCondition(user))
  }

  if (
    user.role === ROLES.COMMUNITY_PRESIDENT ||
    user.role === ROLES.VICE_PRESIDENT ||
    user.role === ROLES.FINANCE ||
    user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.ANALYST ||
    user.role === ROLES.CLINICAL_COORDINATOR
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.EXPECTED_SAVINGS_SPEND)
  }

  if (
    user.role === ROLES.CONTRACT_STEWARD ||
    user.role === ROLES.VICE_PRESIDENT
  ) {
    defineSavingsSpendAbilities(builder, isOwnerCondition(user))
  }
  if (
    user.role === ROLES.LEGAL ||
    user.role === ROLES.ANALYST ||
    user.role === ROLES.HOSPITAL_ADMIN ||
    user.role === ROLES.HOSPITAL_USER ||
    user.role === ROLES.FINANCE ||
    user.role === ROLES.VENDOR_LEGAL ||
    user.role === ROLES.VENDOR_ANALYST ||
    user.role === ROLES.VENDOR_CONTRACT_STEWARD ||
    user.role === ROLES.VENDOR_FINANCE_OFFICER ||
    user.role === ROLES.VENDOR_CONTRACT_ADMIN ||
    user.role === ROLES.COMMONS_LEGAL ||
    user.role === ROLES.COMMONS_ANALYST ||
    user.role === ROLES.COMMONS_CLINICAL_COORDINATOR ||
    user.role === ROLES.COMMONS_STAKEHOLDER
  ) {
    builder.can(ACTIONS.VIEW, SUBJECTS.CONTRACT_CATEGORY)
  }
}
